import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment } from 'react';

import { EditOrganizationFormWrapper } from '@/components/management/form/EditOrganizationFormWrapper';
import { FormGridLayout } from '@/components/management/form/formHelpers/FormGridLayout';
import { AdministrationBody } from '@/components/management/layout/AdministrationBody';
import { AdministrationHeader } from '@/components/management/layout/AdministrationHeader';
import { useAbility } from '@/hooks/useAbility';
import { useScrollSpy } from '@/hooks/useScrollSpy';
import { Action, Subject } from '@/modules/api/backend';
import {
  backendApiOrganizationsFindOneOptions,
  backendApiOrganizationsRemoveMutation,
  backendApiOrganizationsUpdateMutation,
} from '@/modules/api/backend/@tanstack/react-query.gen';
import { useConfirmStore } from '@/stores/useConfirmStore';
import { useSnackbarStore } from '@/stores/useSnackbarStore';

export const Route = createFileRoute('/_authenticated/management/organizations/$organizationId/')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
  beforeLoad: () => ({ breadcrumb: null }),
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { organizationId } = Route.useParams();

  const { ability } = useAbility();

  const { data: organizationData } = useSuspenseQuery(
    backendApiOrganizationsFindOneOptions({
      path: { organizationId },
      query: { users: true },
    }),
  );

  const { openSnackbar } = useSnackbarStore((state) => state.actions);
  const { openConfirm } = useConfirmStore((state) => state.actions);

  const { mutateAsync: mutateAsyncUpdate } = useMutation({
    ...backendApiOrganizationsUpdateMutation(),
    onSuccess: () => {
      openSnackbar({
        color: 'success',
        message: 'Die Änderungen wurden erfolgreich gespeichert!',
      });
    },
    onError: () => {
      openSnackbar({
        color: 'error',
        message: 'Fehler beim Speichern.',
      });
    },
  });

  const { mutateAsync: mutateAsyncDelete } = useMutation({
    ...backendApiOrganizationsRemoveMutation(),
    meta: {
      invalidateQueries: false,
    },
    onSuccess: () => {
      openSnackbar({
        color: 'success',
        message: 'Die Organisation wurde erfolgreich gelöscht!',
      });
      navigate({
        to: '/management/organizations',
      });
    },
    onError: () => {
      openSnackbar({
        color: 'error',
        message: 'Fehler beim Löschen der Organisation.',
      });
    },
  });

  const tabs = ['Organisation', 'User*innen'];
  const { setRef, scrollTo, activeIndex } = useScrollSpy<HTMLDivElement>(tabs.length);

  return (
    <Fragment>
      <AdministrationHeader
        formId="organizationForm"
        label="Organisation bearbeiten"
        onDelete={
          ability?.can(Action.MANAGE, Subject.All)
            ? async () => {
                const confirmed = await openConfirm({
                  title: 'Organisation löschen',
                  message: 'Sind Sie sicher, dass Sie diese Organisation unwiderruflich löschen möchten?',
                  confirmText: 'Löschen',
                  cancelText: 'Abbrechen',
                });

                if (confirmed) {
                  await mutateAsyncDelete({
                    path: { organizationId },
                  });
                }
              }
            : undefined
        }
      />
      <AdministrationBody>
        <FormGridLayout activeTab={activeIndex} onTabChange={scrollTo} tabs={tabs}>
          {organizationData ? (
            <EditOrganizationFormWrapper
              organizationData={organizationData}
              setRef={setRef}
              tabs={tabs}
              onSubmit={async (values) => {
                await mutateAsyncUpdate({
                  path: { organizationId },
                  body: values,
                });
              }}
            />
          ) : null}
        </FormGridLayout>
      </AdministrationBody>
    </Fragment>
  );
}
